import { api, getApiBaseUrl } from "./apiClient";
import { getToken } from "@/utils/auth";

export interface PeriodosResponse {
  periodos: string[];
  meses_otorgamiento: string[];
  buckets: string[];
  productos: string[];
}

export interface ReporteTabularItem {
  monto_colocacion: number;
  "Capital Vencido": { [bucket: string]: number };
  "Interes Vencido": { [bucket: string]: number };
  "Total Vencido": { [bucket: string]: number };
}

export interface ReporteTabularResponse {
  periodos: string[];
  meses_otorgamiento: string[];
  buckets: string[];
  productos: string[];
  renglones: {
    [grupo: string]: {
      [renglon: string]: ReporteTabularItem;
    };
  };
  contenido_tipo: string;
  accion: string;
}

export interface CosechaFiltros {
  producto?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  contenido_tipo?: string;
  accion?: string;
}

export interface CosechaFiltrosGrafica {
  producto?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  contenido_tipo?: string;
  agrupacion?: 'periodo' | 'otorgamiento';
  tipo_grafica?: 'line' | 'bar';
}

export interface DatasetGrafica {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
}

export interface GraficaCosechasResponse {
  labels: string[];
  datasets: DatasetGrafica[];
  contenido_tipo: string;
  agrupacion: string;
}

export const cosechaService = {
  /**
   * Sube un archivo CSV con datos de cosechas
   */
  uploadCSV: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    // No establecer Content-Type manualmente, el navegador lo hace automáticamente para FormData
    return api.post<{ message: string; status: string }>("/cosechas/upload", formData);
  },

  /**
   * Obtiene listas de períodos, meses de otorgamiento, buckets y productos disponibles
   */
  getPeriodos: (fechaInicio?: string, fechaFin?: string) => {
    const params = new URLSearchParams();
    if (fechaInicio) params.set("fecha_inicio", fechaInicio);
    if (fechaFin) params.set("fecha_fin", fechaFin);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get<PeriodosResponse>(`/cosechas/periodos${suffix}`);
  },

  /**
   * Obtiene reporte tabular de cosechas
   */
  getTabular: (filtros: CosechaFiltros) => {
    const params = new URLSearchParams();
    if (filtros.producto) params.set("producto", filtros.producto);
    if (filtros.fecha_inicio) params.set("fecha_inicio", filtros.fecha_inicio);
    if (filtros.fecha_fin) params.set("fecha_fin", filtros.fecha_fin);
    if (filtros.contenido_tipo) params.set("contenido_tipo", filtros.contenido_tipo);
    if (filtros.accion) params.set("accion", filtros.accion);
    return api.get<ReporteTabularResponse>(`/cosechas/tabular?${params.toString()}`);
  },

  /**
   * Exporta el reporte tabular a CSV
   */
  exportCSV: async (filtros: CosechaFiltros) => {
    const params = new URLSearchParams();
    if (filtros.producto) params.set("producto", filtros.producto);
    if (filtros.fecha_inicio) params.set("fecha_inicio", filtros.fecha_inicio);
    if (filtros.fecha_fin) params.set("fecha_fin", filtros.fecha_fin);
    if (filtros.contenido_tipo) params.set("contenido_tipo", filtros.contenido_tipo);
    if (filtros.accion) params.set("accion", filtros.accion);

    const token = getToken();
    const headers: HeadersInit = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(`${getApiBaseUrl()}/cosechas/export-csv?${params.toString()}`, {
      credentials: "include",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Error al exportar CSV: ${response.statusText}`);
    }
    
    // Crear blob y descargar
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Reporte_Cosechas_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Obtiene datos para gráfica de cosechas
   */
  getGrafica: (filtros: CosechaFiltrosGrafica) => {
    const params = new URLSearchParams();
    if (filtros.producto) params.set("producto", filtros.producto);
    if (filtros.fecha_inicio) params.set("fecha_inicio", filtros.fecha_inicio);
    if (filtros.fecha_fin) params.set("fecha_fin", filtros.fecha_fin);
    if (filtros.contenido_tipo) params.set("contenido_tipo", filtros.contenido_tipo);
    if (filtros.agrupacion) params.set("agrupacion", filtros.agrupacion);
    if (filtros.tipo_grafica) params.set("tipo_grafica", filtros.tipo_grafica);
    return api.get<GraficaCosechasResponse>(`/cosechas/grafica?${params.toString()}`);
  },
};
